import React, { PureComponent } from 'react';
import SchedulingList from './scheduling/SchedulingList';
import SchedulingMap from './map/SchedulingMap';
import { convertProjection4329, isMultiPolygon } from '../utils';
import { LocalAtRecord } from '../db';
import { Boundary, ScheduleMap, JobBoundsMap, ComplexPolygonCoords, MultiPolygonCoords } from '../types/types';
import S3 from '../aws';
import { alertWarn } from '../alertDispatcher';
import { RobotsFieldSet, RigsFieldSet, ShiftDropoffsFieldSet } from '@rogoag/airtable/crm';
import { MINIMUM_COORDINATES_FOR_VALID_BOUNDARY } from '../constants';
import { restartPeriodicDbSync } from '../db/datamanager';
import { TaskName } from '../db/TaskClass';
import { parseGeoJsonText } from '../utilities/geojson-utils';
import { Equipment } from '@rogoag/airtable/equipment';

interface ScheduledTaskProps {
  task: TaskName;
}
interface SchedulingTaskState {
  unassigned: LocalAtRecord[];
  scheduled: ScheduleMap;
  robotRefreshRateSeconds: number;
  loading: boolean;
  selectedShift: string;
  hiddenScheduled: string[];
  filtered: LocalAtRecord[];
  jobBounds: JobBoundsMap;
  allRobots: LocalAtRecord<RobotsFieldSet>[];
  allTrucks: Equipment[];
  allRigs: LocalAtRecord<RigsFieldSet>[];
  availableShifts: LocalAtRecord[];
  shiftDropoffs: LocalAtRecord<ShiftDropoffsFieldSet>[];
  allDropoffs: LocalAtRecord[];
  showDropoffs: boolean;
  showRobots: boolean;
  showTrucks: boolean;
  showJobs: boolean;
  zoomField?: LocalAtRecord;
}

export default class SchedulingTask extends PureComponent<ScheduledTaskProps, SchedulingTaskState> {
  constructor(props: ScheduledTaskProps) {
    super(props);

    this.state = {
      showDropoffs: false,
      showRobots: true,
      showTrucks: true,
      showJobs: true,
      selectedShift: 'Unassigned',
      robotRefreshRateSeconds: 60,
      zoomField: undefined,
      loading: false,
      availableShifts: [],
      hiddenScheduled: [],
      allRobots: [],
      allTrucks: [],
      allRigs: [],
      jobBounds: {},
      unassigned: new Array<LocalAtRecord>(),
      shiftDropoffs: [],
      scheduled: {},
      filtered: [],
      allDropoffs: [],
    };

    this.updateScheduled = this.updateScheduled.bind(this);
    this.updateUnassigned = this.updateUnassigned.bind(this);
    this.updateDisplayFilter = this.updateDisplayFilter.bind(this);
    this.processBoundary = this.processBoundary.bind(this);
    this.processFieldBoundaries = this.processFieldBoundaries.bind(this);
    this.updateLoading = this.updateLoading.bind(this);
    this.updateAvailableShifts = this.updateAvailableShifts.bind(this);
    this.updateShiftDropoffs = this.updateShiftDropoffs.bind(this);
    this.updateAllRobots = this.updateAllRobots.bind(this);
    this.updateAllTrucks = this.updateAllTrucks.bind(this);
    this.updateAllRigs = this.updateAllRigs.bind(this);
    this.updateHiddenScheduled = this.updateHiddenScheduled.bind(this);
    this.updateAllDropoffs = this.updateAllDropoffs.bind(this);
    this.toggleShowJobs = this.toggleShowJobs.bind(this);
    this.toggleShowDropoffs = this.toggleShowDropoffs.bind(this);
    this.toggleShowRobots = this.toggleShowRobots.bind(this);
    this.toggleShowTrucks = this.toggleShowTrucks.bind(this);
    this.updateZoomField = this.updateZoomField.bind(this);
    this.updateSelectedShift = this.updateSelectedShift.bind(this);
    this.updateRobotRefreshRate = this.updateRobotRefreshRate.bind(this);
  }

  componentDidMount(): void {
    restartPeriodicDbSync(this.props.task);
  }

  processParsedShape(parsedShp: GeoJSON.FeatureCollection<GeoJSON.Geometry> | null, bounds: Boundary[]): void {
    if (!parsedShp || parsedShp.type !== 'FeatureCollection') {
      return;
    }

    for (const feature of parsedShp.features) {
      if (!feature.geometry) {
        continue;
      }

      if (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon') {
        continue;
      }

      const geomCoordinates: ComplexPolygonCoords | MultiPolygonCoords = feature.geometry.coordinates;

      if (geomCoordinates.length === 0) {
        continue;
      }

      if (isMultiPolygon(geomCoordinates, feature.geometry.type)) {
        const hasInvalidBoundaries = geomCoordinates.some((complexPolygon) =>
          complexPolygon.some((ring) => ring.length < MINIMUM_COORDINATES_FOR_VALID_BOUNDARY),
        );

        if (hasInvalidBoundaries) {
          continue;
        }

        bounds.push({
          type: 'MultiPolygon',
          coords: geomCoordinates.map((complexPolygon) =>
            complexPolygon.map((ring) => ring.map((coords) => convertProjection4329(coords))),
          ),
        });
      } else if (feature.geometry.type === 'Polygon') {
        const hasInvalidBoundaries = geomCoordinates.some(
          (ring) => ring.length < MINIMUM_COORDINATES_FOR_VALID_BOUNDARY,
        );

        if (hasInvalidBoundaries) {
          continue;
        }

        bounds.push({
          type: 'Polygon',
          coords: geomCoordinates.map((ring) => ring.map((coords) => convertProjection4329(coords))),
        });
      }
    }
  }

  async processBoundary(job: LocalAtRecord): Promise<Boundary[]> {
    const bounds: Boundary[] = [];

    if (job.table !== 'Jobs') {
      return [];
    }

    if (job.fields['Bnd GeoJSON Text']) {
      try {
        const parsedGeoJson = parseGeoJsonText(job.fields['Bnd GeoJSON Text']);

        this.processParsedShape(parsedGeoJson, bounds);
      } catch (e) {
        console.error(e);
      }

      return bounds;
    }

    if (!job.fields['Bnd GeoJSON']) {
      return [];
    }

    // TODO This is bad practice, technically we only store one file here but direct referencing is a bad idea
    // we should put a warning here for this case
    if (job.fields['Bnd GeoJSON'].length > 1) {
      alertWarn('More than one boundary file found for job: ' + job.id);
    }

    const jobAttachment = job.fields['Bnd GeoJSON'][0]; // Have to directly retrieve this because it is an attachment

    if (!jobAttachment) {
      return [];
    }

    let url: string;

    // Some attachments already have the job id part in the filename. If not, add it.
    const jobFileKey = jobAttachment.filename.includes(`${job.id}/`)
      ? jobAttachment.filename
      : `${job.id}/${jobAttachment.filename}`;

    if (await S3.checkIfS3FileExists(jobFileKey)) {
      url = S3.urlFor(jobFileKey);
    } else {
      url = jobAttachment.url;
    }

    try {
      const fetched = await fetch(url);
      const parsedShp = await fetched.json();
      this.processParsedShape(parsedShp, bounds);
    } catch (e) {
      console.error(e);
    }

    return bounds;
  }

  async processFieldBoundaries(unassigned: LocalAtRecord[], scheduled: ScheduleMap) {
    const jobs = [
      ...Object.values(scheduled)
        .flat()
        .filter((r) => r.table === 'Jobs'),
      ...unassigned.filter((r) => r.table === 'Jobs'),
    ];

    const jobBounds = { ...this.state.jobBounds };

    await Promise.all(
      jobs.map(async (job) => {
        await new Promise<void>(async (resolve, reject) => {
          if (jobBounds[job.id] !== undefined) {
            resolve();

            return;
          }

          jobBounds[job.id] = await this.processBoundary(job);

          resolve();
        });
      }),
    );

    this.setState({ jobBounds });
  }

  updateLoading(loading: boolean) {
    this.setState({ loading });
  }

  updateSelectedShift(selectedShift: string) {
    this.setState({ selectedShift });
  }

  updateAvailableShifts(availableShifts) {
    this.setState({ availableShifts });
  }

  updateHiddenScheduled(hiddenScheduled: string[]) {
    this.setState({ hiddenScheduled });
  }

  updateAllRobots(allRobots) {
    this.setState({ allRobots });
  }

  updateAllTrucks(allTrucks) {
    this.setState({ allTrucks });
  }

  updateAllRigs(allRigs) {
    this.setState({ allRigs });
  }

  updateRobotRefreshRate(robotRefreshRateSeconds: number) {
    this.setState({ robotRefreshRateSeconds });
  }

  updateZoomField(zoomField) {
    this.setState({ zoomField });
  }

  updateScheduled(scheduled: ScheduleMap) {
    this.setState({ scheduled });
  }

  updateUnassigned(unassigned: LocalAtRecord[]) {
    this.setState({ unassigned });
  }

  updateDisplayFilter(filtered: LocalAtRecord[]) {
    this.setState({ filtered });
  }

  updateShiftDropoffs(shiftDropoffs: LocalAtRecord<ShiftDropoffsFieldSet>[]) {
    this.setState({ shiftDropoffs });
  }

  updateAllDropoffs(allDropoffs) {
    this.setState({ allDropoffs });
  }

  toggleShowDropoffs() {
    this.setState({ showDropoffs: !this.state.showDropoffs });
  }

  toggleShowRobots() {
    this.setState({ showRobots: !this.state.showRobots });
  }

  toggleShowTrucks() {
    this.setState({ showTrucks: !this.state.showTrucks });
  }

  toggleShowJobs() {
    this.setState({ showJobs: !this.state.showJobs });
  }

  render() {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
        <SchedulingList
          updateAllRobots={this.updateAllRobots}
          updateAllTrucks={this.updateAllTrucks}
          updateAllRigs={this.updateAllRigs}
          updateRobotRefreshRate={this.updateRobotRefreshRate}
          updateZoomField={this.updateZoomField}
          updateHiddenScheduled={this.updateHiddenScheduled}
          updateLoading={this.updateLoading}
          toggleShowDropoffs={this.toggleShowDropoffs}
          toggleShowRobots={this.toggleShowRobots}
          toggleShowTrucks={this.toggleShowTrucks}
          updateDisplayFilter={this.updateDisplayFilter}
          updateAvailableShifts={this.updateAvailableShifts}
          updateShiftDropoffs={this.updateShiftDropoffs}
          updateAllDropoffs={this.updateAllDropoffs}
          processFieldBoundaries={this.processFieldBoundaries}
          updateUnassigned={this.updateUnassigned}
          updateScheduled={this.updateScheduled}
          toggleShowJobs={this.toggleShowJobs}
          updateSelectedShift={this.updateSelectedShift}
          loading={this.state.loading}
          hiddenScheduled={this.state.hiddenScheduled}
          allRobots={this.state.allRobots}
          allTrucks={this.state.allTrucks}
          allRigs={this.state.allRigs}
          selectedShift={this.state.selectedShift}
          unassigned={this.state.unassigned}
          scheduled={this.state.scheduled}
          filtered={this.state.filtered}
          shiftDropoffs={this.state.shiftDropoffs}
          availableShifts={this.state.availableShifts}
          allDropoffs={this.state.allDropoffs}
          showJobs={this.state.showJobs}
          showDropoffs={this.state.showDropoffs}
          showRobots={this.state.showRobots}
          showTrucks={this.state.showTrucks}
          robotRefreshRate={this.state.robotRefreshRateSeconds}
        />
        <SchedulingMap
          updateLoading={this.updateLoading}
          updateScheduled={this.updateScheduled}
          updateUnassigned={this.updateUnassigned}
          selectedShift={this.state.selectedShift}
          availableShifts={this.state.availableShifts}
          allRobots={this.state.allRobots}
          allTrucks={this.state.allTrucks}
          unassigned={this.state.unassigned}
          filtered={this.state.filtered}
          zoomField={this.state.zoomField}
          hiddenScheduled={this.state.hiddenScheduled}
          scheduled={this.state.scheduled}
          showJobs={this.state.showJobs}
          showDropoffs={this.state.showDropoffs}
          showRobots={this.state.showRobots}
          showTrucks={this.state.showTrucks}
          jobBounds={this.state.jobBounds}
          robotRefreshRate={this.state.robotRefreshRateSeconds}
        />
      </div>
    );
  }
}
